import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { api } from '../api';
import { laoDateTime } from '../lib/money';
import type { WebhookDelivery } from '../types';

/** ໂຫຼດໃໝ່ທຸກ 20 ວິນາທີ ເພື່ອກວດ webhook ທີ່ສົ່ງບໍ່ສຳເລັດ */
const NOTIFY_POLL_MS = 20_000;

interface LayoutProps {
  title: string;
  appName: string;
  onSignOut: () => void;
  children: ReactNode;
}

const NAV: [string, string, ReactNode][] = [
  ['/', 'ພາບລວມ', <OverviewIcon key="i" />],
  ['/payments', 'ປະຫວັດການຊຳລະ', <HistoryIcon key="i" />],
  ['/new', 'ສ້າງ QR', <QrIcon key="i" />],
  ['/logs', 'ແຈ້ງເຕືອນ ແລະ Webhook', <BellIcon key="i" />],
  ['/settings', 'ຕັ້ງຄ່າ', <SettingsIcon key="i" />],
];

export function Layout({ title, appName, onSignOut, children }: LayoutProps) {
  const [navOpen, setNavOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [failed, setFailed] = useState<WebhookDelivery[]>([]);
  const notifyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = `${title} | ${appName}`;
  }, [title, appName]);

  useEffect(() => {
    let active = true;
    const load = () =>
      api
        .webhooks()
        .then(({ deliveries }) => {
          if (active) setFailed(deliveries.filter((d) => d.status === 'failed'));
        })
        .catch(() => {
          /* ບໍ່ຕ້ອງລາຍງານ error ຢູ່ badge — ໜ້າ Logs ຈະສະແດງ error ເອງ */
        });

    void load();
    const timer = setInterval(load, NOTIFY_POLL_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!notifyOpen) return;
    function onDocClick(e: MouseEvent) {
      if (notifyRef.current && !notifyRef.current.contains(e.target as Node)) setNotifyOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setNotifyOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [notifyOpen]);

  const failedCount = failed.length;

  return (
    <div className="shell">
      <aside className={`sidebar${navOpen ? ' open' : ''}`}>
        <div className="sidebar-brand">
          <img src="/logo-icon.svg" alt="" className="sidebar-logo" width={34} height={34} />
          <span className="sidebar-brand-name">{appName}</span>
        </div>
        <nav className="sidebar-nav" aria-label="ເມນູຫຼັກ">
          {NAV.map(([to, label, icon]) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className="sidebar-link"
              onClick={() => setNavOpen(false)}
            >
              <span className="sidebar-link-icon">{icon}</span>
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <button type="button" className="sidebar-signout" onClick={onSignOut}>
          <SignOutIcon />
          <span>ອອກຈາກລະບົບ</span>
        </button>
      </aside>

      <div className="shell-main">
        <header className="topbar">
          <button
            type="button"
            className="topbar-menu-btn"
            aria-label="ເປີດເມນູ"
            onClick={() => setNavOpen((v) => !v)}
          >
            <MenuIcon />
          </button>
          <span className="topbar-title">{title}</span>
          <div className="notify" ref={notifyRef}>
            <button
              type="button"
              className="topbar-notify-btn"
              aria-haspopup="true"
              aria-expanded={notifyOpen}
              aria-label={
                failedCount > 0 ? `ແຈ້ງເຕືອນ — ${failedCount} webhook ສົ່ງບໍ່ສຳເລັດ` : 'ແຈ້ງເຕືອນ'
              }
              onClick={() => setNotifyOpen((v) => !v)}
            >
              <BellIcon />
              {failedCount > 0 && (
                <span className="topbar-notify-badge">{failedCount > 9 ? '9+' : failedCount}</span>
              )}
            </button>

            {notifyOpen && (
              <div className="notify-dropdown" role="menu">
                <div className="notify-dropdown-head">ແຈ້ງເຕືອນ</div>
                {failed.length === 0 ? (
                  <p className="notify-dropdown-empty muted">ບໍ່ມີແຈ້ງເຕືອນຄ້າງ</p>
                ) : (
                  <ul className="notify-dropdown-list">
                    {failed.slice(0, 5).map((d) => (
                      <li key={d.id}>
                        <p className="notify-dropdown-title">
                          {d.event} ສົ່ງບໍ່ສຳເລັດ{d.paymentId && ` · ${d.paymentId}`}
                        </p>
                        <p className="muted">{laoDateTime(d.createdAt)}</p>
                      </li>
                    ))}
                  </ul>
                )}
                <Link to="/logs" className="notify-dropdown-all" onClick={() => setNotifyOpen(false)}>
                  ເບິ່ງທັງໝົດ
                </Link>
              </div>
            )}
          </div>
        </header>
        <main className="content">
          <div className="content-inner">{children}</div>
        </main>
      </div>

      {navOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="ປິດເມນູ"
          onClick={() => setNavOpen(false)}
        />
      )}
    </div>
  );
}

export function StatusChip({ status, label }: { status: string; label: string }) {
  const tone =
    status === 'paid' || status === 'delivered'
      ? 'paid'
      : status === 'pending' || status === 'queued'
        ? 'submitted'
        : status === 'failed'
          ? 'failed'
          : 'pending';
  return <span className={`chip chip-${tone}`}>{label}</span>;
}

export function ErrorBox({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p className="alert" role="alert">
      {error}
    </p>
  );
}

export function EmptyState({ message }: { message: string }) {
  return (
    <div className="empty-state">
      <BoxIcon />
      <p>{message}</p>
    </div>
  );
}

const ICON_PROPS = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function OverviewIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M4 19V10M12 19V5M20 19v-6" />
    </svg>
  );
}
function HistoryIcon() {
  return (
    <svg {...ICON_PROPS}>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 9.5h17M8 3v3M16 3v3M7 13h5M7 16.5h3" />
    </svg>
  );
}
function QrIcon() {
  return (
    <svg {...ICON_PROPS}>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1" />
      <path d="M14 14h3v3h-3zM19 14h1.5M14 19h1.5M19 19h1.5" />
    </svg>
  );
}
function SettingsIcon() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0A1.65 1.65 0 0 0 10 3.09V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0c.26.6.83 1 1.51 1H21a2 2 0 1 1 0 4h-.09c-.68 0-1.25.4-1.51 1z" />
    </svg>
  );
}
function SignOutIcon() {
  return (
    <svg {...ICON_PROPS} width={18} height={18}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </svg>
  );
}
function BellIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M6 8a6 6 0 1 1 12 0c0 4 1.5 5.5 2 6.5H4c.5-1 2-2.5 2-6.5Z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </svg>
  );
}
function MenuIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}
function BoxIcon() {
  return (
    <svg width={96} height={96} viewBox="0 0 96 96" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 32 48 16l36 16-36 16-36-16Z" />
      <path d="M12 32v34l36 16 36-16V32" />
      <path d="M48 48v34" />
      <path d="M30 24l36 16" />
    </svg>
  );
}
